import type { OnboardingState, OnboardingStepInput } from "@iec-id/shared/contracts/profile";
import { Button } from "@iec-id/ui";
import type { FormEvent, ReactNode } from "react";
import { useOnboardingT } from "../onboarding-i18n";

/** Черновик мастера — то, что человек уже ввёл на прошлых заходах. */

/** Общий набор пропов у всех шагов: мутацию держит роут, шаг только рисует. */
export interface StepProps {
	state: OnboardingState;
	submit: (input: OnboardingStepInput) => void;
	pending: boolean;
	/** Готовый текст ошибки отправки (не ключ) либо `null`. */
	error: string | null;
	/**
	 * Перейти к финалу, не отправляя шаг. Нужен ровно одному случаю: принимать
	 * на шаге документов уже нечего, а мастер обязан закончиться.
	 */
	skip: () => void;
}

/**
 * Форма шага: поля, ошибка отправки и одна кнопка внизу. Возврат живёт в шапке
 * карточки (`OnboardingCard`), а не здесь: внизу он соперничал по весу с
 * действием шага.
 *
 * Именно `<form>`, а не `<div>` с кнопкой: на телефоне Enter с экранной
 * клавиатуры обязан отправлять шаг, иначе мастер требует дотянуться до кнопки
 * после каждого поля.
 */
export function StepForm({
	onSubmit,
	pending,
	error,
	submitLabel,
	disabled = false,
	children,
}: {
	onSubmit: () => void;
	pending: boolean;
	error: string | null;
	submitLabel?: string;
	disabled?: boolean;
	children: ReactNode;
}) {
	const t = useOnboardingT();

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();
		onSubmit();
	};

	return (
		<form noValidate className="flex flex-col gap-5" onSubmit={handleSubmit}>
			{/* Поля — одна группа с зазором 12; до кнопки остаются секционные 20.
			    Пока всё стояло на 20, поле от поля отличалось не больше, чем поле
			    от действия (ритм — в шапке `auth-card.tsx`). */}
			<div className="flex flex-col gap-3">{children}</div>

			{error ? (
				<p role="alert" className="t-body text-danger">
					{error}
				</p>
			) : null}

			<Button
				type="submit"
				variant="primary"
				fullWidth
				state={pending ? "loading" : disabled ? "disabled" : "default"}
			>
				{submitLabel ?? t("actions.continue")}
			</Button>
		</form>
	);
}
